import type { ElementType } from 'react'
import {
  TicketIcon,
  ServerIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CogIcon,
  MapIcon,
} from '@heroicons/react/20/solid'

export interface ProductSection {
  services?: string[]
  schemes?: string[]
  devices?: string[]
  keyFeatures?: string[]
  capabilities?: string[]
  supportedAreas?: string[]
  benefits?: string[]
}

export interface Product {
  id: string
  name: string
  tagline: string
  shortDesc: string
  description: string[]
  bullets: string[]
  icon: ElementType
  sections?: ProductSection
}

export const products: Product[] = [
  {
    id: 'l3-transit',
    name: 'Payment L3 POS',
    tagline: 'Simplify your payment ecosystem with our ready-to-use L3 Transit App',
    shortDesc: 'We deliver future-ready payment solutions powered by Smart Cards, EMV, NFC QR technology.',
    icon: TicketIcon,
    description: [
      'We provide end-to-end **Payment L3 Application Development** Services for banks, payment processors, terminal vendors, fintech companies, and merchants, enabling secure and compliant payment acceptance across multiple payment schemes and terminal platforms.',
      'Our expertise covers the design, development, customization, integration, testing, and certification of Level 3 (L3) payment applications for traditional POS, Android Smart POS, SoftPOS, unattended terminals, and self-service payment devices.',
      '**Supports any type of OEM (Kernel) hardware**'
    ],
    bullets: [
      'Ready to use customizable L3 Payment apps',
      'Multi-scheme support: Visa, Mastercard, Amex, UnionPay, Rupay, JCB, Discover and local schemes',
      'Fast certification turnaround',
      'Support for ATM, POS, Transit, mPOS',
    ],
    sections: {
      services: [
        'Payment L3 Application Design and Development',
        'EMV Contact and Contactless Transaction Processing',
        'Multi-Scheme Payment Application Development',
        'Android POS and Smart POS Application Development',
        'SoftPOS Application Development',
        'Transit and Closed-Loop Payment Applications',
        'Value-Added Services Integration',
        'Host and Payment Switch Integration',
        'Terminal Management System (TMS) Integration',
        'Application Maintenance and Enhancement',
      ],
      schemes: [
        'Visa',
        'Mastercard',
        'Rupay',
        'American Express',
        'Discover',
        'JCB',
        'UnionPay',
        'Domestic and Regional Payment Schemes',
      ],
      keyFeatures: [
        'Purchase, Refund, Reversal, and Void Transactions',
        'Balance Inquiry and Cash Advance Support',
        'Contact, Contactless, Mobile Wallet, and QR Payments',
        'Multi-Acquirer and Multi-Merchant Support',
        'Dynamic Currency Conversion (DCC)',
        'Loyalty and Value-Added Services Integration',
        'Secure Transaction Processing',
        'Offline and Online Transaction Support',
        'Comprehensive Reporting and Audit Trails',
      ],
      benefits: [
        'Accelerated time-to-market',
        'Reduced development and certification effort',
        'Compliance with international and domestic payment standards',
        'Enhanced transaction security and reliability',
        'Seamless integration with payment ecosystems',
        'Scalable and future-ready payment solutions',
      ],
    },
  },
  {
    id: 'transit-payment',
    name: 'Transit Payment Application',
    tagline: 'Open-loop, closed-loop, and account-based fare collection for modern transit networks',
    shortDesc:
      'End-to-end transit fare collection solutions — from contactless bank cards and mobile wallets to closed-loop transit cards — built for frictionless passenger experiences.',
    icon: MapIcon,
    description: [
      'We provide specialized **Transit Payment Application Development Services** for public transportation operators, transit authorities, banks, payment processors, and system integrators, enabling seamless and secure fare collection across modern transit networks.',
      'Our solutions support open-loop, closed-loop, and account-based ticketing systems, allowing passengers to pay using contactless bank cards, mobile wallets, transit cards, QR codes, and other digital payment methods.',
    ],
    bullets: [
      'Fast and secure contactless fare payments',
      'EMV Contactless and mobile wallet support',
      'Multi-operator and multi-route support',
      'Offline and online transaction processing',
    ],
    sections: {
      services: [
        'Transit Payment Application Design and Development',
        'Open-Loop Fare Collection Solutions',
        'Closed-Loop Transit Card Solutions',
        'Account-Based Ticketing (ABT) Integration',
        'EMV Contactless Transit Application Development',
        'Mobile Ticketing and QR Payment Solutions',
        'Validator and Gate Device Integration',
        'Transit Host and Clearing System Integration',
        'Testing, Certification, and Deployment Support',
      ],
      keyFeatures: [
        'Fast and secure contactless fare payments',
        'EMV Contactless card acceptance',
        'Mobile wallet support (NFC and QR)',
        'Offline and online transaction processing',
        'Multi-operator and multi-route support',
      ],
      benefits: [
        'Improved passenger experience through faster boarding',
        'Reduced cash handling and operational costs',
        'Increased fare collection efficiency',
        'Support for international and domestic payment schemes',
        'Scalable architecture for growing transit networks',
        'Seamless integration with existing transport infrastructure',
      ],
    },
  },
  {
    id: 'tms',
    name: 'Terminal Management System',
    tagline: 'Centralized platform to configure, monitor, and update your entire terminal fleet',
    shortDesc:
      'Help payment organizations efficiently manage their terminal infrastructure, ensuring secure, reliable, and scalable payment acceptance across their entire network.',
    icon: ServerIcon,
    description: [
      'Our **Terminal Management System (TMS)** enables banks, acquirers, payment processors, and terminal vendors to efficiently manage, monitor, configure, and update their payment terminals from a centralized platform.',
      'Designed to support large-scale terminal deployments, the solution provides secure remote management capabilities, helping organizations reduce operational costs, improve terminal uptime, and accelerate the rollout of new services and payment applications.',
      'Our Multi-Brand TMS enables banks, acquirers, payment processors, and merchants to manage POS terminals from multiple vendors through one unified interface, simplifying operations and reducing management complexity.',
    ],
    bullets: [
      'Centralized multi-vendor terminal management',
      'Real-time monitoring and inventory tracking',
      'Remote parameter deployment',
      'Cloud-based architecture for scalability and security',
    ],
    sections: {
      keyFeatures: [
        'Centralized terminal inventory management',
        'Remote terminal configuration and parameter updates',
        'Payment application deployment and version control',
        'EMV and scheme parameter management',
        'Remote software and firmware updates',
        'Terminal health monitoring and diagnostics',
        'Real-time status tracking and reporting',
        'Secure key and certificate management',
        'Estate management and performance analytics',
        'Automated campaign and update scheduling',
      ],
      devices: [
        'POS Terminals',
        'Android Smart POS Devices',
        'SoftPOS Solutions',
        'Self-Service Kiosks',
        'Unattended Payment Terminals',
        'ATM and Banking Devices',
      ],
      benefits: [
        'Centralized management of terminal estates',
        'Reduced operational and maintenance costs',
        'Faster deployment of applications and updates',
        'Improved terminal availability and performance',
        'Enhanced security and compliance',
        'Simplified scheme and regulatory updates',
        'Increased visibility and control across the payment network',
      ],
      services: [
        'TMS Implementation and Integration',
        'Custom TMS Development',
        'Payment Application Distribution',
        'Remote Key Injection Solutions',
        'Terminal Estate Migration',
        'TMS Support and Managed Services',
        'Integration with Host Systems and Payment Switches',
      ],
    },
  },
  {
    id: 'emv-kernels',
    name: 'EMV Kernels',
    tagline: 'Scheme-certified, secure, and customizable kernels built for speed and compliance',
    shortDesc: 'Our Payment Kernel Development services enable organizations to deliver robust, scalable, and future-ready payment acceptance solutions that meet the evolving demands of the global payments industry.',
    icon: DocumentTextIcon,
    description: [
      'We provide specialized **Payment Kernel Development Services** for terminal vendors, payment processors, fintech companies, banks, and payment solution providers. Our expertise covers the design, development, customization, integration, and maintenance of EMV Contact and Contactless Kernels that comply with international and domestic payment scheme requirements.',
      'With extensive experience in payment technologies, we help organizations build secure, high-performance, and certification-ready payment solutions that enable seamless transaction acceptance across multiple payment networks.',
    ],
    bullets: [
      'EMV Contact and Contactless kernels',
      'Built in C/C++ — easily portable',
      'Configured per customer ICS',
      'Detailed API documentation',
    ],
    sections: {
      services: [
        'EMV Contact Kernel Development',
        'Contactless Kernel Development and Integration',
        'Scheme-Specific Kernel Implementation',
        'Kernel Customization and Optimization',
        'Payment Application Integration',
        'SoftPOS Kernel Integration',
        'Transit and Closed-Loop Payment Kernel Solutions',
        'Kernel Migration and Upgrade Services',
        'Performance Testing and Validation',
        'L2 Certification Support',
      ],
      schemes: [
        'Visa',
        'Mastercard',
        'Rupay',
        'American Express',
        'Discover',
        'JCB',
        'UnionPay',
        'Domestic Payment Schemes',
      ],
      keyFeatures: [
        'EMV-compliant transaction processing',
        'Contact and Contactless payment support',
        'Multi-scheme acceptance capability',
        'Secure cryptographic processing',
        'High-performance transaction execution',
        'Flexible integration with payment applications',
        'Certification-ready architecture',
      ],
      benefits: [
        'Accelerated product development and deployment',
        'Reduced certification timelines and costs',
        'Enhanced security and compliance',
        'Improved transaction performance and reliability',
        'Seamless interoperability across payment ecosystems',
        'Faster time-to-market for innovative payment solutions',
      ],
    },
  },
  {
    id: 'host-simulators',
    name: 'Network Host Simulators',
    tagline: 'Smart simulators for quick, cost-effective testing and certification',
    shortDesc:
      'Whether you are developing a new payment application, certifying a POS terminal, integrating with a payment processor, or validating host connectivity, our Host Simulator solutions provide the flexibility and reliability required for successful project delivery.',
    icon: CreditCardIcon,
    description: [
      'We provide advanced **Host Simulator Services** to help organizations test and validate payment systems in a controlled environment before connecting to live production hosts. Our Host Simulator solutions that enable banks, payment processors, terminal vendors, and payment application developers to test and validate their payment systems in a controlled environment before connecting to live production hosts.',
      'Our host simulator emulates issuer and acquirer host systems, supporting end-to-end transaction testing, ISO 8583 message validation, EMV transaction processing, and scheme-specific certification requirements. This helps organizations accelerate development, reduce certification timelines, and ensure reliable payment acceptance across multiple payment networks.',
    ],
    bullets: [
      'Acquiring and Issuing simulation',
      'Real-time data and full crypto support',
      'Configurable response profiles',
      'EMV-format log export',
    ],
    sections: {
      keyFeatures: [
        'ISO 8583 message simulation and validation',
        'Issuer and acquirer host emulation',
        'Purchase, refund, reversal, and balance inquiry transaction testing',
        'EMV Contact and Contactless transaction support',
        'Domestic and international scheme certification testing',
        'Online and offline authorization scenarios',
        'Real-time transaction monitoring and debugging',
        'Custom test case and certification scenario support',
      ],
      benefits: [
        'Faster product development and deployment',
        'Reduced dependency on live host environments',
        'Improved transaction quality and reliability',
        'Accelerated certification and compliance processes',
        'Lower testing and operational costs',
        'Enhanced interoperability across payment ecosystems',
      ],
    },
  },
  {
    id: 'emv-l3-test',
    name: 'Payment Application Test Platform',
    tagline: 'Test, validate, and certify payment applications with confidence across all major schemes and devices',
    shortDesc:
      'Helps organizations deliver secure, compliant, and market-ready payment solutions with confidence, reducing time-to-market while ensuring the highest standards of payment acceptance and interoperability.',
    icon: CogIcon,
    description: [
      'Our **Payment Application Test Platform** is a comprehensive solution for testing, validating, and certifying payment applications across multiple payment schemes and acceptance devices. The Payment Application Test Platform provides a comprehensive environment for developing, testing, validating, and certifying payment applications across multiple payment schemes and acceptance devices.',
      'Designed for banks, payment processors, terminal vendors, fintechs, and payment application developers, the platform enables end-to-end testing of EMV, contactless, mobile, and card-based payment transactions in a secure and controlled environment.',
    ],
    bullets: [
      'Automated test case execution for all major schemes',
      'Detailed transaction log analysis and reporting',
      'Support for POS, ATM, Transit, and mPOS terminals',
      'Integrated certification report generation',
    ],
    sections: {
      capabilities: [
        'End-to-end payment application testing',
        'EMV Contact and Contactless transaction validation',
        'ISO 8583 message testing and analysis',
        'Host simulator integration for online transaction testing',
        'Terminal and payment application pre-certification support',
        'Regression and interoperability testing',
        'Real-time transaction monitoring and log analysis',
        'Automated test execution and reporting',
      ],
      supportedAreas: [
        'POS Payment Applications',
        'SoftPOS Solutions',
        'ATM Applications',
        'Mobile Payment Applications',
        'Transit and Closed-Loop Payment Systems',
        'Domestic and International Payment Schemes',
      ],
      benefits: [
        'Accelerate payment application development',
        'Reduce certification timelines and costs',
        'Improve transaction quality and reliability',
        'Ensure compliance with scheme requirements',
        'Identify and resolve issues early in the development cycle',
        'Simplify testing across multiple devices and payment networks',
      ],
    },
  },
]
